import type {Geometry} from "geojson";
import type {
    ILayerView,
    IDefaultLayerView,
    LayerInfo,
    IScheduleLayerView,
    GeoLocationMeta
} from "@esWebAPI/es/model/Layer";
import type {Criteria} from "@esWebAPI/es/model/Criteria";
import type {Attribute, Feature, SortOrder} from "@esWebAPI/es/model/Feature";
import type {IImportMeta} from "@esWebAPI/es/model/Models";


/**
 * Model object used to store details of a layer view and alert interested parties when changes occur
 *
 * @param layerView layerView object
 */
export default class LayerViewModel {

    private eventChannel = es.util.createEventChannel({
        onSetAttributeNames: 1,
        onSetCriteria: 1,
        onSetDescription: 1,
        onSetDynamicAttributes: 1,
        onSetEnabled: 1,
        onSetMinResolution: 1,
        onSetOrdering: 1,
        onSetSelectCriteria: 1,
        onSetSortOrder: 1,
        onSetStyleSettings: 1,
        onSetThematicableAttributeNames: 1,
        onSetTitle: 1,
        onSetShiftId: 1,
        onSetTransientCriteria: 1,
        onUpdate: 1 // TODO: rename this onUpdateLayerViewModel
        //onBatchEditFeatures: 1 not used?
    }, "es.map.LayerViewModel");

    private layerView: ILayerView;
    private layerInfo: LayerInfo;

    private $type: ILayerView["$type"];
    private geoLocationMeta: GeoLocationMeta|undefined;

    private id: number;
    private title: string;
    private description: string;
    private minResolution: number;
    private maxResolution: number;
    private geomExportable: boolean;
    private geomType: Uppercase<Geometry["type"]>;
    private copyright: string;
    private ordering: number;
    private styleSettings: any;
    private criteria: Criteria;
    private sortOrder: SortOrder;
    private attributeNames: string[];
    private thematicableAttributeNames: string[];
    private dynamicAttributes: any;
    private enabled: boolean;
    private selectable: boolean;
    private selectCriteria: Criteria|null;
    private importMeta: IImportMeta;
    private shiftId: string|undefined;

    private transientCriteria: any = null;

    private updateLayerViewQueue: {dfd: JQuery.Deferred<void>, newLayerViewProps: any}[] = [];

    constructor(layerView: ILayerView, layerInfo: LayerInfo) {

        this.layerView = layerView;
        this.layerInfo = layerInfo;
        this.id = layerView.id;
        this.title = layerView.title;
        this.description = layerView.description;
        this.minResolution = layerView.minResolution;
        this.maxResolution = layerView.maxResolution ;
        this.geomExportable = layerView.geomExportable;
        this.geomType = layerView.geomType;
        this.copyright = layerView.copyright;
        this.ordering = layerView.ordering;
        this.styleSettings = es.util.object.deepCopy(layerView.styleSettings);
        this.criteria = layerView.criteria ? es.util.object.deepCopy(layerView.criteria) : layerView.criteria;
        this.sortOrder = layerView.sortOrder ? es.util.object.deepCopy(layerView.sortOrder) : layerView.sortOrder;
        this.attributeNames = layerView.attributeNames ? $.extend(true, [], layerView.attributeNames) : layerView.attributeNames;
        this.thematicableAttributeNames = layerView.thematicableAttributeNames ? $.extend(true, [], layerView.thematicableAttributeNames) : layerView.thematicableAttributeNames;
        this.dynamicAttributes = layerView.dynamicAttributes ? $.extend(true, [], layerView.dynamicAttributes) : [];
        this.enabled = layerView.enabled;
        this.selectable = layerView.selectable;
        this.selectCriteria = layerView.selectCriteria;
        this.importMeta = layerView.importMeta;
        this.shiftId = (layerView as IScheduleLayerView).shiftId;

        if (layerView.$type) {
            this.$type = layerView.$type;

            if ((layerView as IDefaultLayerView).geoLocationMeta) {
                this.geoLocationMeta = (layerView as IDefaultLayerView).geoLocationMeta;
            }
        } else {
            throw new Error("We only accept LayerViews on this method");
        }

        es.api.getEventChannel()
            .addListener("onUpdateLayerView", this.onUpdateLayerView, this)
            .addListener("onUpdateLayerViewRemovableCriteria", this.onUpdateLayerViewRemovableCriteria, this)
            .addListener("onGetLayerInfo", this.onGetLayerInfo, this);

        if (layerView.layerMetaId) {
            const layerMetaId = layerView.layerMetaId;
            es.api.webSocket?.addListener?.(this.onWebsocketMessageListener, this);
            es.api.webSocket?.addOpenListener?.(() => {
                es.api.webSocket?.send?.({'$type': 'ListenDatasetChanges', 'id': layerMetaId});
            });
        }
    }

    private initLayerView(newLayerView: ILayerView, newLayerInfo: LayerInfo, fireOnUpdate: boolean, forceUpdate?: boolean) {
        let eventNames = [];
        if (fireOnUpdate) {
            if (this.title != newLayerView.title) eventNames.push("onSetTitle");
            if (this.shiftId != (newLayerView as IScheduleLayerView).shiftId) eventNames.push("onSetShiftId");
            if (this.description != newLayerView.description) eventNames.push("onSetDescription");
            if (!es.util.object.equals(this.styleSettings, newLayerView.styleSettings)) eventNames.push("onSetStyleSettings");
            if (!es.util.object.equals(this.criteria, newLayerView.criteria)) eventNames.push("onSetCriteria");
            if (!es.util.object.equals(this.sortOrder, newLayerView.sortOrder)) eventNames.push("onSetSortOrder");
            if (!es.util.object.equals(this.attributeNames, newLayerView.attributeNames)) eventNames.push("onSetAttributeNames");
            if (!es.util.object.equals(this.thematicableAttributeNames, newLayerView.thematicableAttributeNames)) eventNames.push("onSetThematicableAttributeNames");
            if (!es.util.object.equals(this.dynamicAttributes, newLayerView.dynamicAttributes)) eventNames.push("onSetDynamicAttributes");
            if (!this.enabled != !newLayerView.enabled) eventNames.push("onSetEnabled");
            if (this.ordering != newLayerView.ordering) eventNames.push("onSetOrdering");
            if (this.selectCriteria != newLayerView.selectCriteria) eventNames.push("onSetSelectCriteria");
        }

        this.id = newLayerView.id;
        this.title = newLayerView.title;
        this.description = newLayerView.description;
        this.minResolution = newLayerView.minResolution;
        this.maxResolution = newLayerView.maxResolution ;
        this.geomExportable = newLayerView.geomExportable;
        this.geomType = newLayerView.geomType;
        this.copyright = newLayerView.copyright;
        this.ordering = newLayerView.ordering;
        this.styleSettings = es.util.object.deepCopy(newLayerView.styleSettings);
        this.criteria = newLayerView.criteria ? es.util.object.deepCopy(newLayerView.criteria) : newLayerView.criteria;
        this.sortOrder = newLayerView.sortOrder ? es.util.object.deepCopy(newLayerView.sortOrder) : newLayerView.sortOrder;
        this.attributeNames = newLayerView.attributeNames ? $.extend(true, [], newLayerView.attributeNames) : newLayerView.attributeNames;
        this.thematicableAttributeNames = newLayerView.thematicableAttributeNames ? $.extend(true, [], newLayerView.thematicableAttributeNames) : newLayerView.thematicableAttributeNames;
        this.dynamicAttributes = newLayerView.dynamicAttributes ? $.extend(true, [], newLayerView.dynamicAttributes) : [];
        this.enabled = newLayerView.enabled;
        this.selectable = newLayerView.selectable;
        this.selectCriteria = newLayerView.selectCriteria;
        this.importMeta = newLayerView.importMeta;

        this.layerView = newLayerView;
        this.layerInfo = newLayerInfo;

        this.shiftId = (newLayerView as IScheduleLayerView).shiftId;

        if (fireOnUpdate && (forceUpdate || (eventNames.length > 0))) {
            window.setTimeout(() => {
                this.eventChannel.fireEvent("onUpdate", this, true, eventNames);
            }, 1); // only fire onUpdate after all callbacks are processed
        }
    }

    private onUpdateLayerView(layerView: ILayerView, layerInfo: LayerInfo) {
        if (this.id == layerView.id) {
            layerView = es.util.object.deepCopy(layerView);
            layerInfo = es.util.object.deepCopy(layerInfo);
            let forceUpdate = false;
            if (layerView.lastModified > layerView.lastModified) {
                forceUpdate = true; // if the last modified has changed, then we need to force an update.
            }
            this.initLayerView(layerView, layerInfo, true, forceUpdate);
        }
    }

    private onUpdateLayerViewRemovableCriteria(layerViewRemovableCriteria: {created: number, id:number, lastModified: number, removableCriteria: Criteria}){
        if (this.layerView.featureSourceId == layerViewRemovableCriteria.id) {
            this.layerView.lastModified = layerViewRemovableCriteria.lastModified;
            this.layerInfo.featureInfo.lastModified = layerViewRemovableCriteria.lastModified; // need this here or cache will be wrong
            this.getRefreshedLayerInfo().done((layerInfo) => {
                this.layerInfo = layerInfo;
                this.layerInfo.featureInfo.lastModified = layerViewRemovableCriteria.lastModified;
                this.eventChannel.fireEvent("onUpdate", this, true, ["onSetRemovableCriteria"]);
            });
        }
    }

    private onGetLayerInfo(layerInfo: LayerInfo) {
        if (layerInfo.layerViewId == this.id) {
            this.layerInfo = layerInfo;
        }
    }

    /**
     * Get the type of this layer view
     * @returns type
     */
    public getType() {
        return this.$type;
    }

    /**
     * Get the title for this layer view
     * @returns Title
     */
    public getTitle() {
        return this.title;
    }

    /**
     * Set the layer view title
     *
     * @param title
     * @return this
     */
    public setTitle(title: string) {
        this.updateLayerView({'title': title});
        return this;
    }

    /**
     * Get the description for this layer view
     * @returns description
     */
    public getDescription(){
        return this.description;
    }

    /**
     * Set the layer view description
     *
     * @param description
     * @return this
     */
    public setDescription(description: string) {
        this.updateLayerView({'description': description});
        return this;
    }

    /**
     * Get the minResolution for this layer view
     * @returns minResolution
     */
    public getMinResolution(){
        return this.minResolution;
    }

    /**
     * Set the layer view minResolution
     *
     * @param minResolution
     * @return this
     */
    public setMinResolution(minResolution: number) {
        this.updateLayerView({'minResolution': minResolution});
        return this;
    }

    /**
     * Get the maxResolution for this layer view
     * @returns maxResolution
     */
    public getMaxResolution() {
        return this.maxResolution;
    }

    /**
     * Set the layer view maxResolution
     *
     * @param maxResolution
     * @return this
     */
    public setMaxResolution(maxResolution: number) {
        this.updateLayerView({'maxResolution': maxResolution});
        return this;
    };

    /**
     * Get the geomExportable for this layer view
     * @returns geomExportable
     */
    public isGeomExportable() {
        return this.geomExportable;
    }

    /**
     * Set the layer view geomExportable
     *
     * @param geomExportable
     * @return this
     */
    public setGeomExportable(geomExportable: boolean) {
        this.updateLayerView({'geomExportable': geomExportable});
        return this;
    }

    /**
     * Get the geomType for this layer view
     * @returns geomType
     */
    public getGeomType() {
        return this.geomType;
    }

    /**
     * Set the layer view geomType
     *
     * @param geomType
     * @return this
     */
    public setGeomType(geomType: Uppercase<Geometry["type"]>) {
        this.updateLayerView({'geomType': geomType});
        return this;
    }

    /**
     * Get the copyright for this layer view
     * @returns copyright
     */
    public getCopyright() {
        return this.copyright;
    }

    /**
     * Get the id for this layer view
     *
     * @return
     */
    public getId() {
        return this.id;
    }

    /**
     * Get the layer meta id for this layer view
     *
     * @return
     */
    public getLayerMetaId() {
        return this.layerView.layerMetaId;
    }

    /**
     * Get the geo map id for this layer view
     *
     * @return
     */
    public getGeoMapId() {
        return this.layerView.geoMapId;
    }

    /**
     * Get the last modified time for this layer view
     *
     * @return
     */
    public getLastModified() {
        const ret = this.layerView.lastModified ? this.layerView.lastModified : 0;
        return Math.max(ret, this.layerInfo.featureInfo.lastModified);
    }

    /**
     * Get the ordering for this layerview
     *
     * @return
     */
    public getOrdering() {
        return this.ordering;
    }

    /**
     * Set the layer ordering
     *
     * @param ordering
     * @return this
     */
    public setOrdering(ordering: number) {
        this.updateLayerView({'ordering': ordering});
        return this;
    }

    /**
     * Get the event channel for this model
     *
     * @return
     */
    public getEventChannel() {
        return this.eventChannel;
    }

    /**
     * Get the info for this layer
     *
     * @return
     */
    public getLayerInfo(): LayerInfo {
        function asAttribute(dynamicAttr: any) {
            if (!dynamicAttr) {
                return null;
            }

            return {
                'name': dynamicAttr.name,
                'title': dynamicAttr.title,
                'attrType': dynamicAttr.type,
                'internal': false,
                'maxSize': 0
            } as Attribute;
        }

        const layerInfoAttributes: Attribute[] = [];
        if (this.attributeNames) {
            this.attributeNames.forEach(attrName => {
                const attr = this.layerInfo.attributes.get('name', attrName) || asAttribute(this.dynamicAttributes.get('name', attrName));
                if (!attr) {
                    es.log.warn('Unexpected error: could not get layer info for attr ' + attrName, this.layerInfo); // TODO: should never happen!!!
                } else {
                    layerInfoAttributes.push(attr);
                }
            });
        }

        return {
            'layerViewId': this.layerInfo.layerViewId,
            'layerMetaId': this.layerInfo.layerMetaId,
            'attributes': es.util.object.deepCopy(layerInfoAttributes),
            'featureInfo': es.util.object.deepCopy(this.layerInfo.featureInfo)
        };
    };

    /**
     * Get the timestamp when features were last modified
     */
    public getFeaturesModified () {
        return this.layerInfo.featureInfo.lastModified;
    }

    /**
     * Set the timestamp when features were last modified
     */
    public setFeaturesModified(timestamp: number) {
        this.layerInfo.featureInfo.lastModified = timestamp;
    }

    /**
     * set the feature count
     */
    public setFeatureCount(count: number) {
        this.layerInfo.featureInfo.count = count;
    }

    /**
     * set the bounds
     */
    public setBounds(bounds: number[]) {
        this.layerInfo.featureInfo.bounds = bounds;
    }

    public getGeoLocationMetaObjRef() {
        return this.geoLocationMeta;
    }

    /**
     * Set the styleSettings for the layer view
     *
     * @param newStyleSettings
     * @return this
     */
    public setStyleSettings(newStyleSettings: any) {
        return this.updateLayerView({'styleSettings': newStyleSettings}).done(() => {});

    }

    /**
     * Get the styleSettings for the layer view
     *
     * @return
     */
    public getStyleSettingsClone() {
        return es.util.object.deepCopy(this.styleSettings);
    }

    /**
     * NOTE: this returns modifiable styleSettings obj
     * @returns {*}
     */
    public getStyleSettingsObjRef() {
        return this.styleSettings
    }

    /**
     * Get the criteria for the layer view
     *
     * @return
     */
    public getCriteriaClone() {
        return es.util.object.deepCopy(this.criteria);
    }

    /**
     * NOTE: this returns modifiable territory obj
     * @returns {*}
     */
    public getCriteriaObjRef() {
        return this.criteria;
    }

    /**
     * Set the criteria for the layer view
     *
     * @param newCriteria
     * @return this
     */
    public setCriteria(newCriteria: Criteria) {
        this.updateLayerView({'criteria': newCriteria});
        return this;
    }

    /**
     * Get the transient criteria for the layer view
     *
     * @return
     */
    public getTransientCriteria() {
        return this.transientCriteria;
    }

    /**
     * Set the transient criteria for the layer view. Transient criteria should never be saved.
     *
     * @param newTransientCriteria
     * @return this
     */
    public setTransientCriteria(newTransientCriteria: Criteria) {
        if (!es.util.object.equals(newTransientCriteria, this.transientCriteria)) {
            this.transientCriteria = newTransientCriteria ? es.util.object.deepCopy(newTransientCriteria) : null;
            this.eventChannel.fireEvent("onSetTransientCriteria", newTransientCriteria);
            this.eventChannel.fireEvent("onUpdate", this, true, ["onSetTransientCriteria"]);
        }
        return this;
    }

    /**
     * Get criteria - both transient and regular
     */
    public getAllCriteria() {
        return es.util.criteria.and(this.getCriteriaClone(), this.transientCriteria);
    };

    /**
     * Get the sort order for the layer view
     *
     * @return
     */
    public getSortOrder() {
        return es.util.object.deepCopy(this.sortOrder);
    }

    /**
     * Set the sort order for the layer view
     *
     * @param newSortOrder
     * @return this
     */
    public setSortOrder(newSortOrder: SortOrder) {
        this.updateLayerView({'sortOrder': newSortOrder});
        return this;
    }

    /**
     * Get the attribute names for the layer view
     *
     * @return
     */
    public getAttributeNames(): string[] {
        return $.extend(true, [], this.attributeNames);
    }

    /**
     * Checks if attribute names for the layer view model are overwritten (i.e. are not the same as saved server side)
     */
    public isAttributeNamesOverwritten() {
        return !es.util.object.equals(this.layerView.attributeNames, this.attributeNames);
    }

    /**
     * Set the attribute names for the layer view
     *
     * @param newAttributeNames
     * @return deferred object
     */
    public setAttributeNames(newAttributeNames: string[]) {
        return this.updateLayerView({'attributeNames': newAttributeNames});
    }

    public setImportMeta(importMeta: any) {
        return this.updateLayerView({'importMeta': importMeta});
    }

    /**
     * Get the thematicable attribute names for the layer view
     *
     * @return
     */
    public getThematicableAttributeNames(): string[] {
        if (!this.thematicableAttributeNames) {
            return this.thematicableAttributeNames;
        }

        return $.extend(true, [], this.thematicableAttributeNames);
    }

    /**
     * Get the dynamic attributes for the layer view
     */
    public getDynamicAttributes() {
        return $.extend(true, [], this.dynamicAttributes);
    }

    // this.setDynamicAttributes is not specified. Should use updateLayerView method to set it.

    /**
     * Get whether the layer view is enabled
     *
     * @return
     */
    public isEnabled() {
        return this.enabled;
    }

    /**
     * Get whether the layer view is selectable
     *
     * @return
     */
    public isSelectable() {
        return this.selectable;
    }

    /**
     * Set the layer view selectable flag
     * @param newSelectable
     *
     */
    public setSelectable(newSelectable: boolean) {
        this.updateLayerView({'selectable': newSelectable});
        return this;
    }

    public getSelectCriteria() {
        return this.selectCriteria;
    }

    public setSelectCriteria (newSelectCriteria: Criteria|null) {
        this.selectCriteria = newSelectCriteria;
    }

    /**
     * Set whether the layer view is enabled and visible
     *
     * @param newEnabled
     * @return this
     */
    public setEnabled(newEnabled: boolean) {
        this.updateLayerView({'enabled': newEnabled});
        return this;
    }

    /**
     * Get the layer view import meta
     *
     * @return
     */
    public getImportMetaClone(): IImportMeta {
        return es.util.object.deepCopy(this.layerView.importMeta);
    }

    public getImportMetaObjRef() {
        return this.layerView.importMeta;
    }

    public getShiftId() {
        return this.shiftId;
    }

    public setShiftId(shiftId: string) {
        this.updateLayerView({shiftId});
        return this;
    }

    /**
     * Get the layer territory meta id
     */
    public getTerritoryMetaId() {
        return this.layerView.territoryMetaId;
    };


    /**
     * Load the feature with the id given
     */
    public loadFeature(featureId: number) {
        if (this.layerView.id > 0) {
            return $.when(
                es.api.feature.get(
                    this.layerView.id,
                    featureId,
                    this.getAttributeNames(),
                    es.util.object.equals(this.dynamicAttributes, this.layerView.dynamicAttributes) ? null : this.dynamicAttributes,
                    4326
                ),
                es.map.vector.style.buildStyler(this.getLayerView())  // TODO FIXME Do we need to set the style here? Shouldn't it be the caller?
            ).then((feature: Feature, styler: any) => {
                if (feature) { // If we add a feature that won't show on the map because of layer view criteria, we request it and got nothing...
                    feature.style = es.map.vector.style.getStyle(styler, null, feature, this.layerInfo);
                }

                return feature;
            });
        }

        const request = this.getRequest(true);
        request.criteria = es.util.criteria.and(request.criteria, {
            '$type': 'Equal',
            'attr': 'ES_ID',
            'value': featureId
        });

        return $.when(
            es.api.feature.render({
                'layers': [request],
                'format': {
                    'getAttrs': true
                }
            }),
            es.map.vector.style.buildStyler(this.getLayerView())  // TODO FIXME Do we need to set the style here? Shouldn't it be the caller?
        ).then((rs, styler) => {
            if (!rs.layers || !rs.layers.length || !rs.layers[0].features || !rs.layers[0].features.length) {
                return es.util.deferred.rejected('Unknown feature: ' + featureId);
            }

            const feature = rs.layers[0].features[0];
            feature.style = es.map.vector.style.getStyle(styler, null, feature, this.layerInfo);

            return feature;
        });
    }

    /**
     * Load the features with the ids given
     */
    public loadFeatures(featureIds: number[]) {
        return es.api.feature.getByIds(this.layerView.id, featureIds, this.getAttributeNames(), 4326);
    }

    /**
     * Determine whether this layer view has been customised and requires an explicit request or can be covered by simply requesting
     * the geoMap.
     * @param overrideSortOrder {optional} an override of the sort order that is stored with the layer view
     * @return true if this needs an explicit request
     */
    public needsExplicitRequest(overrideSortOrder: boolean) { //TODO: It would be better to
        return (this.layerView.id < 0) ||
            this.transientCriteria ||
                overrideSortOrder ||
                ((this.styleSettings.$type !== 'TerritoryPointStyleSettings' || !this.styleSettings.useTerritoryColor) && !es.util.object.equals(this.styleSettings, this.layerView.styleSettings)) ||
                (!es.util.object.equals(this.criteria, this.layerView.criteria)) ||
                (!es.util.object.equals(this.sortOrder, this.layerView.sortOrder)) ||
                (!es.util.object.equals(this.attributeNames, this.layerView.attributeNames)) ||
                (!es.util.object.equals(this.ordering, this.layerView.ordering)) ||
                (!es.util.object.equals(this.enabled, this.layerView.enabled));
    }

    /**
     * Get the layer view json object backing this model
     *
     * @return the layer view json object
     */
    public getLayerView(): ILayerView {
        // we have to do 2 deep copies and merge separately as deep copy and merging arrays is not perfect in jquery
        // ($.extend(true, {}, {'a': [1,2,3]}, {'a':[4]}) gives {'a': [4,2,3]} instead of {'a':[4]})
        const a = es.util.object.deepCopy(this.layerView);
        const b = es.util.object.deepCopy({
            '$type': this.$type,
            'title': this.title,
            'description': this.description,
            'minResolution': this.minResolution,
            'maxResolution': this.maxResolution,
            'geomExportable': this.geomExportable,
            'geomType': this.geomType,
            'copyright': this.copyright,
            'ordering' : this.ordering,
            'styleSettings': this.styleSettings,
            'criteria': this.criteria,
            'sortOrder': this.sortOrder,
            'attributeNames': this.attributeNames,
            'dynamicAttributes': this.dynamicAttributes,
            'selectable': this.selectable,
            'importMeta': this.importMeta,
            'selectCriteria': this.selectCriteria,
            'enabled': this.enabled,
        });

        if(this.shiftId) {
            b.shiftId = this.shiftId;
        }

        const lv = $.extend(a, b);

        if (this.thematicableAttributeNames) {
            lv.thematicableAttributeNames = es.util.object.deepCopy(this.thematicableAttributeNames);
        }

        // TODO FIXME if b has some attributes set to undefined instead of null, it won't overwrite a properties
        // TODO FIXME is it only criteria that can be undefined?
        if (this.criteria === undefined) {
            delete lv.criteria;
        }

        return lv;
    }

    /**
     * Get the request for this layer view]
     *
     * @param explicitCriteria {optional} flag indicating that layers should be returned explicitly along with their criteria
     * @param overrideSortOrder {optional} an override of the sort order that is stored with the layer view
     * @return the request for this layer view
     */
    public getRequest(explicitCriteria?: boolean, overrideSortOrder?: SortOrder) {
        const ret: any = {
            id: this.layerView.id
        };

        if (this.layerView.id <= 0) {
            ret.layerMetaId = this.layerView.layerMetaId;
        }
        if (this.styleSettings.$type == 'TerritoryPointStyleSettings' && this.styleSettings.useTerritoryColor) {
            var ss = es.util.object.deepCopy(this.styleSettings);
            delete ss.colorThematicSettings;
            delete ss.heatMapSettings;
            // TODO FIXME should we also delete conditionalStyleSettings and colorPerFeatureSettings. can they exist??!!
            ret.styleSettings = ss;
        } else if (!es.util.object.equals(this.styleSettings, this.layerView.styleSettings)) {
            ret.styleSettings = es.util.object.deepCopy(this.styleSettings);
        }
        if (this.transientCriteria && (this.transientCriteria.$type !== 'All')) {
            ret.criteria = es.util.criteria.and(this.transientCriteria, this.criteria);
        } else if (explicitCriteria || (!es.util.object.equals(this.criteria, this.layerView.criteria))) {
            ret.criteria = this.criteria ? es.util.object.deepCopy(this.criteria) : {'$type': 'All'};
        }

        if (overrideSortOrder){
            ret.sortOrder = overrideSortOrder;
        } else if (!es.util.object.equals(this.sortOrder, this.layerView.sortOrder)) {
            ret.sortOrder = es.util.object.deepCopy(this.sortOrder);
        }
        if (!es.util.object.equals(this.attributeNames, this.layerView.attributeNames)) {
            ret.attributeNames = es.util.object.deepCopy(this.attributeNames);
        }
        if(!es.util.object.equals(this.dynamicAttributes, this.layerView.dynamicAttributes)){
            ret.dynamicAttributes = es.util.object.deepCopy(this.dynamicAttributes);
        }

        //Ordering in request is based on the order of the requested layers - not the specified ordering - this is used only for the geoMap
        //if (ordering !== layerView.ordering) {
        //    ret.ordering = ordering;
        //}
        if(!es.util.object.equals(this.enabled, this.layerView.enabled)) {
            ret.enabled = this.enabled;
        }
        if(!es.util.object.equals(this.minResolution, this.layerView.minResolution)) {
            ret.minResolution = this.minResolution;
        }
        return ret;
    }

    /**
     * Sets new properties values for the layerview. It triggers the properties events (e.g. onSetStyleSettings) only after all changes are made,
     * and it triggers only one onUpdate event in the end.
     *
     * @param newLayerViewProps
     * @return deferred object
     */
    public updateLayerView = (newLayerViewProps: any) => {
        const dfd = $.Deferred();

        this.updateLayerViewQueue.push({
            'dfd': dfd,
            'newLayerViewProps': es.util.object.deepCopy(newLayerViewProps)
        });

        if (this.updateLayerViewQueue.length == 1) {
            this.processUpdateLayerViewQueue();
        }

        return dfd.promise();
    }

    private processUpdateLayerViewQueue() {
        const newLayerViewProps = this.updateLayerViewQueue[0].newLayerViewProps;
        const dfd = this.updateLayerViewQueue[0].dfd;

        this._updateLayerView(newLayerViewProps, dfd).always(() => {
            this.updateLayerViewQueue.splice(0, 1);

            if (this.updateLayerViewQueue.length) {
                this.processUpdateLayerViewQueue();
            }
        });
    }

    private _updateLayerView(newLayerViewProps: any, dfd: JQuery.Deferred<void>) {
        const newLayerViewPropsKeys = Object.keys(newLayerViewProps);

        const eventsArgs: any[] = [];
        if (newLayerViewPropsKeys.indexOf('styleSettings') > -1 && !es.util.object.equals(this.styleSettings, newLayerViewProps.styleSettings)) {
            const oldStyleSettings = this.styleSettings;
            this.styleSettings = es.util.object.deepCopy(newLayerViewProps.styleSettings);
            eventsArgs.push(['onSetStyleSettings', es.util.object.deepCopy(this.styleSettings), oldStyleSettings]);
        }

        if (newLayerViewPropsKeys.indexOf('criteria') > -1 && this.criteria != newLayerViewProps.criteria && !es.util.object.equals(this.criteria, newLayerViewProps.criteria)) {
            const oldCriteria = es.util.object.deepCopy(this.criteria);
            this.criteria = newLayerViewProps.criteria ? es.util.object.deepCopy(newLayerViewProps.criteria) : null;
            eventsArgs.push(['onSetCriteria', es.util.object.deepCopy(this.criteria)]);

            if (oldCriteria && this.dynamicAttributes && this.dynamicAttributes.length > 0 && (newLayerViewPropsKeys.indexOf('attributeNames') == -1 || newLayerViewPropsKeys.indexOf('dynamicAttributes') == -1)) {
                const oldInteracts = es.util.criteria.find(oldCriteria, {'$type': 'Interacts'});
                if (oldInteracts?.name) {
                    const wasRemoved = !es.util.criteria.find(this.criteria, {'$type': 'Interacts', 'name': oldInteracts.name});
                    if (wasRemoved) {
                        const dynamicAttributesToRemove = this.dynamicAttributes
                            .filter((da: any) => da.criteriaName == oldInteracts.name)
                            .map((da: any) => da.name);
                        if (dynamicAttributesToRemove.length > 0) {
                            if (newLayerViewPropsKeys.indexOf('attributeNames') == -1) {
                                // fake developer requested removal of dynamic attribute names based on removed criteria
                                newLayerViewPropsKeys.push('attributeNames');
                                newLayerViewProps.attributeNames = this.attributeNames.filter(attrName => !dynamicAttributesToRemove.includes(attrName));
                            }

                            if (newLayerViewPropsKeys.indexOf('dynamicAttributes') == -1) {
                                // fake developer requested removal of dynamic attribute names based on removed criteria
                                newLayerViewPropsKeys.push('dynamicAttributes');
                                newLayerViewProps.dynamicAttributes = this.dynamicAttributes.filter((da: any) => !dynamicAttributesToRemove.includes(da.name));
                            }
                        }
                    }
                }
            }
        }

        if (newLayerViewPropsKeys.indexOf('title') > -1 && !es.util.object.equals(this.title, newLayerViewProps.title)) {
            this.title = newLayerViewProps.title ? newLayerViewProps.title : null;
            eventsArgs.push(['onSetTitle', this.title]);
        }

        if (newLayerViewPropsKeys.indexOf('description') > -1 && !es.util.object.equals(this.description, newLayerViewProps.description)) {
            this.description = newLayerViewProps.description ? newLayerViewProps.description : null;
            eventsArgs.push(['onSetDescription', this.description]);
        }

        if (newLayerViewPropsKeys.indexOf('sortOrder') > -1 && this.sortOrder != newLayerViewProps.sortOrder && !es.util.object.equals(this.sortOrder, newLayerViewProps.sortOrder)) {
            this.sortOrder = es.util.object.deepCopy(newLayerViewProps.sortOrder);
            eventsArgs.push(['onSetSortOrder', es.util.object.deepCopy(this.sortOrder)]);
        }

        if (newLayerViewPropsKeys.indexOf('thematicableAttributeNames') > -1 && !es.util.object.equals(this.thematicableAttributeNames, newLayerViewProps.thematicableAttributeNames)) {
            this.thematicableAttributeNames = $.extend(true, [], newLayerViewProps.thematicableAttributeNames);
            eventsArgs.push(['onSetThematicableAttributeNames', es.util.object.deepCopy(this.thematicableAttributeNames)]);
        }

        if (newLayerViewPropsKeys.indexOf('attributeNames') > -1 && !es.util.object.equals(this.attributeNames, newLayerViewProps.attributeNames)) {
            this.attributeNames = $.extend(true, [], newLayerViewProps.attributeNames);

            if (newLayerViewPropsKeys.indexOf('thematicableAttributeNames') == -1 && this.thematicableAttributeNames && !es.util.array.includesAll(this.attributeNames, this.thematicableAttributeNames)) {
                this.thematicableAttributeNames = this.thematicableAttributeNames.filter(ta => this.attributeNames.includes(ta));
                eventsArgs.push(['onSetThematicableAttributeNames', es.util.object.deepCopy(this.thematicableAttributeNames)]);
            }

            // Update styleSettings if using attribute no longer in use (and styleSettings not being set)
            if (newLayerViewPropsKeys.indexOf('styleSettings') == -1) {
                const oldStyleSettings = es.util.object.deepCopy(this.styleSettings);
                let updatedStyle = false;

                if (this.styleSettings.conditionalStyleSettings) {
                    // TODO FIXME Should never happen, only for routing
                    es.log.error('Unexpected error: found conditionalStyleSettings, implies routing');
                }

                if (this.styleSettings.colorThematicSettings
                        && (!this.attributeNames.includes(this.styleSettings.colorThematicSettings.attributeName)
                            || (this.thematicableAttributeNames && !this.thematicableAttributeNames.includes(this.styleSettings.colorThematicSettings.attributeName)))) {
                    delete this.styleSettings.colorThematicSettings;
                    updatedStyle = true;
                }
                if (this.styleSettings.sizeThematicSettings
                        && (!this.attributeNames.includes(this.styleSettings.sizeThematicSettings.attributeName)
                        || (this.thematicableAttributeNames && !this.thematicableAttributeNames.includes(this.styleSettings.sizeThematicSettings.attributeName)))) {
                    delete this.styleSettings.sizeThematicSettings;
                    updatedStyle = true;
                }
                if (this.styleSettings.symbolThematicSettings
                        && (!this.attributeNames.includes(this.styleSettings.symbolThematicSettings.attributeName)
                            || (this.thematicableAttributeNames && !this.thematicableAttributeNames.includes(this.styleSettings.symbolThematicSettings.attributeName)))) {
                    delete this.styleSettings.symbolThematicSettings;
                    updatedStyle = true;
                }

                if (updatedStyle) {
                    eventsArgs.push(['onSetStyleSettings', es.util.object.deepCopy(this.styleSettings), oldStyleSettings]);
                }
            }


            // Update sort order if using attribute no longer in use
            if (newLayerViewPropsKeys.indexOf('sortOrder') == -1 && this.sortOrder && !this.attributeNames.includes(this.sortOrder.attr)) {
                this.sortOrder = {'attr': 'ES_ID'};

                var eventOverwritten = false;
                for (var i = 0; i < eventsArgs.length && !eventOverwritten; i++) {
                    if (eventsArgs[i][0] == 'onSetSortOrder') {
                        eventsArgs[i][1] = es.util.object.deepCopy(this.sortOrder);
                        eventOverwritten = true;
                    }
                }

                if (!eventOverwritten) {
                    eventsArgs.push(['onSetSortOrder', es.util.object.deepCopy(this.sortOrder)]);
                }
            }

            eventsArgs.push(['onSetAttributeNames', es.util.object.deepCopy(this.attributeNames)]);
        }


        if(newLayerViewProps.hasOwnProperty("shiftId")) {
            this.shiftId = newLayerViewProps.shiftId;
            this.title = "Route for " + this.shiftId;
            eventsArgs.push(["onSetShiftId", this.shiftId]);
            eventsArgs.push(['onSetTitle', this.title]);
        }

        if (newLayerViewPropsKeys.indexOf('dynamicAttributes') > -1 && !es.util.object.equals(this.dynamicAttributes, newLayerViewProps.dynamicAttributes)) {
            const oldDynamicAttributes = this.dynamicAttributes;
            this.dynamicAttributes = $.extend(true, [], newLayerViewProps.dynamicAttributes);
            eventsArgs.push(['onSetDynamicAttributes', es.util.object.deepCopy(this.dynamicAttributes), oldDynamicAttributes]);
        }

        if (newLayerViewPropsKeys.indexOf('enabled') > -1 && this.enabled !== newLayerViewProps.enabled) {
            this.enabled = newLayerViewProps.enabled;
            eventsArgs.push(['onSetEnabled', this.enabled]);
        }

        if (newLayerViewPropsKeys.indexOf('ordering') > -1 && this.ordering !== newLayerViewProps.ordering) {
            this.ordering = newLayerViewProps.ordering;
            eventsArgs.push(['onSetOrdering', this.ordering]);
        }

        if (newLayerViewPropsKeys.indexOf('minResolution') > -1 && this.minResolution !== newLayerViewProps.minResolution) {
            this.minResolution = newLayerViewProps.minResolution;
            eventsArgs.push(['onSetMinResolution', this.minResolution]);
        }

        if (newLayerViewPropsKeys.indexOf('selectable') > -1 && this.selectable !== newLayerViewProps.selectable) {
            this.selectable = newLayerViewProps.selectable; // TODO onSetSelectable doesnt exist yet, maybe because you dont need the map to update for this to take effect
        }

        if (newLayerViewPropsKeys.indexOf('importMeta') > -1 && !es.util.object.equals(this.importMeta, newLayerViewProps.importMeta)) {
            this.importMeta = newLayerViewProps.importMeta;
        }

        let styleDfd = es.util.deferred.resolved();
        // TOR9-9294 - If there's a thematic being applied, get it from the server (so it's created if needs be) before firing events and resolving deferred
        let newStyleSettings = eventsArgs.find(function(a) { return a[0] == 'onSetStyleSettings'; } );
        if (newStyleSettings) {
            newStyleSettings = newStyleSettings[1];

            const dfds = [];

            if (newStyleSettings.colorThematicSettings
                && newStyleSettings.colorThematicSettings.enabled
                && !newStyleSettings.colorThematicSettings.overrideThematic) {
                dfds.push(es.api.thematic.getColorThematic(this.id, newStyleSettings.colorThematicSettings.attributeName));
            }

            if (newStyleSettings.sizeThematicSettings
                && newStyleSettings.sizeThematicSettings.enabled
                && !newStyleSettings.sizeThematicSettings.overrideThematic) {
                dfds.push(es.api.thematic.getSizeThematic(this.id, newStyleSettings.sizeThematicSettings.attributeName));
            }

            if (newStyleSettings.symbolThematicSettings
                && newStyleSettings.symbolThematicSettings.enabled
                && !newStyleSettings.symbolThematicSettings.overrideThematic) {
                dfds.push(es.api.thematic.getSymbolThematic(this.id, newStyleSettings.symbolThematicSettings.attributeName));
            }

            if (dfds.length) {
                styleDfd = $.when.apply($, dfds);
            }
        }

        let layerInfoDfd = Promise.resolve(this.layerInfo);
        if (newLayerViewPropsKeys.indexOf('attributeNames') > -1 && !es.util.object.equals(this.attributeNames, newLayerViewProps.attributeNames)) {
            layerInfoDfd = this.getRefreshedLayerInfo(); // is this required?
        } else {
            layerInfoDfd = this.getRefreshedLayerInfoIfRequired();
        }

        $.when(
            layerInfoDfd,
            styleDfd
        ).then(async (layerInfo) => {
            this.layerInfo = es.util.object.deepCopy(layerInfo);

            if (eventsArgs.length) {
                for (let i = 0; i < eventsArgs.length; i++) {
                    await this.eventChannel.fireEvent.apply(this.eventChannel, eventsArgs[i]);
                }

                this.layerView.lastModified = Date.now();
                await this.eventChannel.fireEvent("onUpdate", this, false, eventsArgs.map((e) => e[0]));
            }

            dfd.resolve();
        }).fail(function() {
            dfd.reject.apply(dfd, arguments as any);
        });

        return dfd.promise();
    }

    private onWebsocketMessageListener(ev: any) {
        if (ev.$type != 'DatasetUpdate' || ev.layerMetaId != this.layerView.layerMetaId) {
            return;
        }

        Promise.all([
            es.api.layerView.get(this.layerView.id, {"noCache": true}),
            this.getRefreshedLayerInfo(),
            es.api.geoMap.get(this.layerView.geoMapId)
        ]).then(([layerView, layerInfo, geoMap]) => {
            es.api.getEventChannel().fireEvent("onUpdateLayerView", layerView, layerInfo, geoMap.lastModified);
        });
    }

    /**
     * Destroy this layer view model
     */
    public destroy() {
        es.api.getEventChannel()
            .removeListener("onUpdateLayerView", this.onUpdateLayerView, this)
            .removeListener("onUpdateLayerViewRemovableCriteria", this.onUpdateLayerViewRemovableCriteria, this)
            .removeListener("onGetLayerInfo", this.onGetLayerInfo, this);

        if (this.layerView.layerMetaId) {
            es.api.webSocket.removeListener(this.onWebsocketMessageListener, this);
        }

        this.eventChannel.destroy();
        this.eventChannel = null;
    }

    public isDestroyed() {
        return this.eventChannel == null;
    }

    private getRefreshedLayerInfoIfRequired() {
        const enabled = this.styleSettings.heatMapSettings?.enabled;
        const attr = this.styleSettings.heatMapSettings?.attr;
        const attrRanges = this.layerInfo?.featureInfo?.attributeRanges[attr]
        if (!enabled || attrRanges) {
            return Promise.resolve(this.layerInfo);
        }

        return es.api.layerInfo.getForLayerView(this.id, attrRanges, {"noCache": true});
    }

    private getRefreshedLayerInfo() {
        let includeAttributeRanges;
        const enabled = this.styleSettings.heatMapSettings?.enabled;
        const attr = this.styleSettings.heatMapSettings?.attr;
        if (enabled && attr) {
            includeAttributeRanges = [attr];
        }

        return es.api.layerInfo.getForLayerView(this.id, includeAttributeRanges, {"noCache": true});
    }
}
